/**
 * Darcula-inspired palette (JetBrains IDEs) for hyper-dashboard dev UI.
 */
export const darculaTheme = {
    bg: '#2B2B2B',
    bgInput: '#45494A',
    border: '#555555',
    borderAccent: '#6897BB',
    fg: '#A9B7C6',
    button: '#4E5254',
    buttonPressed: '#365880',
    keywordOrange: '#CC7832',
    lightPurple: '#B39FE0',
};

export const darculaIconButtonBase = {
    padding: '6px',
    outline: 'none',
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: 1,
    cursor: 'pointer',
    marginRight: '12px',
    color: darculaTheme.fg,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '2rem',
    minHeight: '2rem',
};

/** React inline `style` objects for the HyperDashboard shell (route + language UI). */
export const darculaHyperDashboardStyles = {
    hyperDashboardRootStyle: {
        position: 'relative',
        padding: '10px 20px',
        backgroundColor: darculaTheme.bg,
        color: darculaTheme.fg,
    },
    routeLabelStyle: {
        display: 'block',
        marginBottom: '4px',
        color: darculaTheme.fg,
    },
    routeSelectStyle: {
        display: 'block',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        minHeight: '34px',
        backgroundColor: darculaTheme.bgInput,
        color: darculaTheme.fg,
        border: `1px solid ${darculaTheme.border}`,
        borderRadius: '3px',
        padding: '6px 10px',
    },
    /** State code + Route as two stacks side by side. */
    routeAndStateRowStyle: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: '24px',
        marginBottom: '10px',
    },
    routeControlColumnStyle: {
        flex: '1 1 200px',
        minWidth: 'min(200px, 100%)',
        maxWidth: '320px',
    },
    langToolbarRowStyle: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        marginBottom: '10px',
    },
    langToolbarStyle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    langGlobeIconStyle: {
        color: darculaTheme.fg,
        fontSize: '1.4rem',
        opacity: 0.9,
    },
    langBtnBaseStyle: {
        padding: '4px 12px',
        fontSize: '14px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        borderRadius: '4px',
        borderStyle: 'solid',
        borderWidth: 1,
        cursor: 'pointer',
        marginRight: '8px',
        color: darculaTheme.fg,
        boxSizing: 'border-box',
    },
    langBtnActiveStyle: {
        backgroundColor: darculaTheme.buttonPressed,
        borderColor: darculaTheme.borderAccent,
    },
    langBtnInactiveStyle: {
        backgroundColor: darculaTheme.button,
        borderColor: darculaTheme.border,
    },
};


const sessionTimerStatusColors = {
    active: '#9FD79F',
    prompted: '#EAB377',
    idle: '#EBA8A8',
    disabled: darculaTheme.fg,
};

/** React inline `style` objects for SessionTimerInfo (session-idle QA readout). */
export const darculaSessionTimerStyles = {
    statusColors: sessionTimerStatusColors,
    // Fixed bottom-left layer so QA can watch the countdown while using the app
    // with the dashboard panel closed. pointerEvents:none keeps it from ever
    // intercepting clicks on the UI underneath.
    overlayContainerStyle: {
        position: 'fixed',
        bottom: '16px',
        left: '16px',
        zIndex: 2147483000,
        pointerEvents: 'none',
    },
    rootStyle: {
        width: 'fit-content',
        maxWidth: '390px',
        marginBottom: '10px',
        padding: '15px 19px',
        borderRadius: '9px',
        backgroundColor: darculaTheme.bgInput,
        border: `1px solid ${darculaTheme.border}`,
        color: darculaTheme.fg,
    },
    headerRowStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '11px',
        marginBottom: '10px',
    },
    titleStyle: {
        margin: 0,
        fontSize: '1.55rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: darculaTheme.fg,
        opacity: 0.8,
    },
    statusDotStyle: {
        width: '13px',
        height: '13px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    countdownRowStyle: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '9px',
    },
    countdownValueStyle: {
        fontSize: '3.1rem',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
    },
    countdownLabelStyle: {
        fontSize: '1.4rem',
        color: darculaTheme.fg,
        opacity: 0.7,
    },
    statusTextStyle: {
        marginTop: '3px',
        fontSize: '1.4rem',
        fontWeight: 600,
    },
    metaRowStyle: {
        marginTop: '12px',
        paddingTop: '10px',
        borderTop: `1px solid ${darculaTheme.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        fontSize: '1.3rem',
    },
    metaLineStyle: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '18px',
    },
    metaLabelStyle: {
        color: darculaTheme.fg,
        opacity: 0.7,
    },
    metaValueStyle: {
        color: darculaTheme.fg,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
    },
};

const settingsIconButtonNeutralStyle = {
    backgroundColor: darculaTheme.button,
    borderColor: darculaTheme.border,
};

/** React inline `style` objects for the Settings block (hyper-dashboard localStorage tools). */
export const darculaSettingsStyles = {
    settingsShellBase: {
        padding: '10px',
        borderRadius: '5px',
        position: 'relative',
        marginBottom: '10px',
    },
    settingsShellOpen: {
        backgroundColor: darculaTheme.bgInput,
        border: `1px solid ${darculaTheme.border}`,
        color: darculaTheme.fg,
    },
    settingsShellCollapsed: {
        backgroundColor: 'transparent',
    },
    settingsToolbarRowStyle: {
        marginBottom: '8px',
    },
    settingsAdvancedToolsGroupStyle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0,
        marginTop: '12px',
        marginBottom: '10px',
        paddingLeft: '10px',
        borderLeft: `2px solid ${darculaTheme.borderAccent}`,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    /** Console tools: toggles in one horizontal row (wraps if the panel is narrow). */
    settingsAdvancedToolsTogglesRowStyle: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
    },
    settingRowWrapperInlineClusterStyle: {
        marginBottom: 0,
        marginRight: 0,
    },
    settingRowWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginRight: '10px',
        marginBottom: '10px',
        padding: '6px 12px',
        borderRadius: '4px',
        border: `1px solid ${darculaTheme.border}`,
        backgroundColor: darculaTheme.bg,
        boxShadow: `inset 0 1px 0 ${darculaTheme.border}44`,
    },
    settingsTogglePressed: {
        backgroundColor: darculaTheme.buttonPressed,
        borderColor: darculaTheme.borderAccent,
    },
    settingsToggleReleased: settingsIconButtonNeutralStyle,
    settingsResetButtonStyle: settingsIconButtonNeutralStyle,
    /** Numeric setting: text field (matches shell inputs). */
    settingsNumericInputStyle: {
        width: '56px',
        minHeight: '1.75rem',
        borderRadius: '4px',
        border: `1px solid ${darculaTheme.border}`,
        backgroundColor: darculaTheme.bgInput,
        color: darculaTheme.fg,
        padding: '4px 8px',
        boxSizing: 'border-box',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.25,
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
        outline: 'none',
    },
    settingsNumericInputFocusStyle: {
        borderColor: darculaTheme.borderAccent,
        boxShadow: `0 0 0 1px ${darculaTheme.borderAccent}`,
    },
    /** +/- stepper cluster beside numeric input. */
    settingsStepperGroupStyle: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '3px',
        marginLeft: '2px',
    },
    /** Single stepper control (+ / -). */
    settingsStepperButtonStyle: {
        backgroundColor: darculaTheme.button,
        border: `1px solid ${darculaTheme.border}`,
        color: darculaTheme.fg,
        borderRadius: '4px',
        cursor: 'pointer',
        padding: '2px 0',
        minWidth: '2rem',
        minHeight: '1.75rem',
        fontSize: '15px',
        fontWeight: 600,
        lineHeight: 1,
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    /** DropdownForSettings (select + optional modifier editor). */
    settingsDropdownRootStyle: {
        display: 'inline-block',
        borderRadius: '4px',
        verticalAlign: 'middle',
    },
    settingsDropdownRootModifierStyle: {
        border: `1px solid ${darculaTheme.border}`,
        padding: '8px 10px',
        backgroundColor: darculaTheme.bgInput,
    },
    settingsDropdownSelectRowStyle: {
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '4px',
    },
    settingsDropdownSelectStyle: {
        backgroundColor: darculaTheme.bgInput,
        color: darculaTheme.fg,
        border: `1px solid ${darculaTheme.border}`,
        borderRadius: '4px',
        padding: '5px 10px',
        fontSize: '14px',
        cursor: 'pointer',
        outline: 'none',
        minHeight: '1.75rem',
        boxSizing: 'border-box',
    },
    settingsDropdownModButtonStyle: {
        backgroundColor: darculaTheme.button,
        border: `1px solid ${darculaTheme.border}`,
        color: darculaTheme.fg,
        borderRadius: '4px',
        cursor: 'pointer',
        padding: '4px 10px',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 1.2,
    },
    settingsDropdownRowStyle: {
        position: 'relative',
        marginBottom: '8px',
    },
    settingsDropdownRowWithDeleteStyle: {
        paddingRight: '2.35rem',
    },
    settingsDropdownOptionLabelStyle: {
        display: 'inline-block',
        marginRight: '1rem',
        fontWeight: 700,
        cursor: 'pointer',
        color: darculaTheme.fg,
        fontSize: '14px',
    },
    settingsDropdownOptionLabelSecondaryStyle: {
        color: '#BC6B6B',
    },
    settingsDropdownChipStyle: {
        marginRight: '8px',
        marginBottom: '4px',
        display: 'inline-block',
        border: `1px solid ${darculaTheme.border}`,
        borderRadius: '4px',
        padding: '3px 8px',
        backgroundColor: darculaTheme.bg,
        color: darculaTheme.fg,
        fontSize: '13px',
    },
    settingsDropdownChipKeyStyle: {
        color: darculaTheme.lightPurple,
        fontWeight: 600,
        marginRight: '6px',
    },
    settingsDropdownValueWrapperStyle: {
        cursor: 'pointer',
    },
    settingsDropdownValueTextStyle: {
        display: 'inline-block',
        marginLeft: '0.5rem',
        color: darculaTheme.keywordOrange,
    },
    settingsDropdownEmptyValuePlaceholderStyle: {
        display: 'inline-block',
        marginLeft: '0.5rem',
        width: '12px',
        height: '12px',
        border: `1px dashed ${darculaTheme.borderAccent}`,
        borderRadius: '2px',
        verticalAlign: 'middle',
        cursor: 'pointer',
        backgroundColor: darculaTheme.bgInput,
    },
    settingsDropdownPrimitiveValueStyle: {
        color: darculaTheme.fg,
        fontSize: '13px',
        marginLeft: '4px',
    },
    settingsDropdownDeleteButtonStyle: {
        position: 'absolute',
        right: 0,
        top: 0,
        backgroundColor: darculaTheme.button,
        border: `1px solid ${darculaTheme.border}`,
        color: darculaTheme.fg,
        borderRadius: '50%',
        height: '1.75rem',
        width: '1.75rem',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        boxSizing: 'border-box',
    },
};

/** React inline `style` objects for HyperButtons (SSAP flow paginator + tooltips). */
export const darculaHyperButtonsStyles = {
    paginatorRootStyle: {
        position: 'relative',
        padding: 0,
    },
    /** Wraps one tooltip + button so the tooltip is positioned above that control. */
    paginatorItemWrapperStyle: {
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'top',
        margin: '3px',
    },
    paginatorButtonBase: {
        boxSizing: 'border-box',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minWidth: '2.35rem',
        padding: '10px 14px',
        margin: 0,
        fontSize: '1.4rem',
        fontWeight: 500,
        lineHeight: 1.3,
        borderRadius: '6px',
        borderStyle: 'solid',
        borderWidth: 1,
        outline: 'none',
        cursor: 'pointer',
        color: darculaTheme.fg,
    },
    paginatorTooltipPanelStyle: {
        display: 'none',
        position: 'absolute',
        left: '50%',
        bottom: '100%',
        transform: 'translateX(-50%)',
        marginBottom: '8px',
        width: '320px',
        maxWidth: 'min(320px, calc(100vw - 16px))',
        minHeight: '100px',
        maxHeight: 'min(70vh, 480px)',
        overflowX: 'hidden',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: darculaTheme.bgInput,
        border: `1px solid ${darculaTheme.border}`,
        borderRadius: '6px',
        textAlign: 'left',
        padding: '12px 14px',
        fontSize: '1.2rem',
        lineHeight: 1.45,
        color: darculaTheme.fg,
        zIndex: 500,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
    },
    paginatorTooltipTitleStyle: {
        color: darculaTheme.keywordOrange,
        fontWeight: 700,
    },
    paginatorTooltipComponentStyle: {
        color: darculaTheme.lightPurple,
        fontWeight: 700,
    },
    paginatorTooltipHhmBlockStyle: {
        fontWeight: 700,
        lineHeight: 1.45,
    },
    paginatorButtonSelectedBoxShadow: [
        'inset 0 1px 0 rgba(255, 255, 255, 0.14)',
        `0 0 0 2px ${darculaTheme.lightPurple}`,
        '0 5px 16px rgba(179, 159, 224, 0.38)',
    ].join(', '),
    paginatorButtonHrStyle: {
        padding: 0,
        margin: 0,
    },
};

const managerPageCheckerIdleStyle = {
    backgroundColor: darculaTheme.button,
    borderColor: darculaTheme.border,
};

/** React inline `style` objects for Manager (tool panels + page checker). */
export const darculaManagerStyles = {
    managerRootStyle: {
        padding: 0,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    managerToolbarRowStyle: {
        fontSize: '1.5rem',
        padding: '7px 0',
        display: 'inline-block',
    },
    managerToolButtonStyle: {
        padding: '6px 12px',
        backgroundColor: darculaTheme.button,
        border: `1px solid ${darculaTheme.border}`,
        borderRadius: '6px',
        cursor: 'pointer',
        marginRight: '12px',
        color: darculaTheme.fg,
        fontWeight: 500,
        fontSize: '14px',
        transition: 'all 0.15s ease',
    },
    managerPageCheckerButtonBase: {
        alignSelf: 'flex-start',
        width: 'fit-content',
        maxWidth: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        borderRadius: '6px',
        borderStyle: 'solid',
        borderWidth: 1,
        cursor: 'pointer',
        boxSizing: 'border-box',
        color: darculaTheme.fg,
        fontSize: '1.3125rem',
        fontWeight: 500,
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
    },
    managerPageCheckerRunning: {
        backgroundColor: darculaTheme.buttonPressed,
        borderColor: darculaTheme.borderAccent,
    },
    managerPageCheckerIdle: managerPageCheckerIdleStyle,
};

const blockWrapperBorderRadius = '1rem';

const blockWrapperCircleBtnBase = {
    flexShrink: 0,
    width: '1.8rem',
    height: '1.8rem',
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.15rem',
    lineHeight: 1,
    color: '#1a1a1a',
};

/** React inline `style` objects for BlockWrapper (floating dev tool panels). */
export const darculaBlockWrapperStyles = {
    panelShellBase: {
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#3C3F41',
        width: '550px',
        borderRadius: blockWrapperBorderRadius,
        zIndex: 2500,
        boxSizing: 'border-box',
    },
    titleBarStyle: {
        flexShrink: 0,
        height: 42,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        padding: '0 8px',
        boxSizing: 'border-box',
        backgroundColor: '#45494E',
        borderBottom: '1px solid #5C6269',
        borderTopLeftRadius: blockWrapperBorderRadius,
        borderTopRightRadius: blockWrapperBorderRadius,
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08)',
    },
    /** Drag handle: flex grower to the right of the traffic-light buttons (sole drag target). */
    titleBarGripStyle: {
        flex: 1,
        minWidth: 0,
        alignSelf: 'stretch',
    },
    titleBarActionsStyle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
    },
    circleBtnResetStyle: {
        ...blockWrapperCircleBtnBase,
        backgroundColor: '#28C840',
    },
    circleBtnCloseStyle: {
        ...blockWrapperCircleBtnBase,
        backgroundColor: '#FF5F57',
        fontSize: '1.4rem',
    },
    contentScrollBase: {
        flex: 1,
        minHeight: 0,
        padding: '0 15px',
    },
};

/** React inline `style` objects for Taxes (tax household dev panel). */
export const darculaTaxesStyles = {
    rowHoverBackground: darculaTheme.button,
    rowDefaultBackground: 'transparent',
    titleStyle: {
        margin: '10px 0',
        fontSize: '1.5rem',
        fontWeight: 600,
        color: darculaTheme.fg,
    },
    primaryTaxFilerRowStyle: {
        margin: '10px 0',
        fontSize: '1.2rem',
        cursor: 'pointer',
        color: darculaTheme.fg,
    },
    memberBlockStyle: {
        fontSize: '1.2rem',
        marginBottom: '1.2rem',
        color: darculaTheme.fg,
    },
    memberHeaderRowStyle: {
        padding: '0.2rem 0',
        position: 'relative',
    },
    memberNameStyle: {
        color: darculaTheme.keywordOrange,
    },
    memberMetaPersonIdStyle: {
        position: 'absolute',
        left: '220px',
        fontSize: '1.2rem',
        color: darculaTheme.fg,
    },
    memberMetaGuidStyle: {
        position: 'absolute',
        left: '320px',
        fontSize: '1.2rem',
        color: darculaTheme.fg,
    },
    memberMetaHouseholdIdStyle: {
        position: 'absolute',
        left: '450px',
        fontSize: '1.2rem',
        color: darculaTheme.fg,
    },
    compositionRowStyle: {
        position: 'relative',
    },
    compositionLabelStyle: {
        color: darculaTheme.lightPurple,
    },
    compositionValueStyle: {
        position: 'absolute',
        left: '230px',
        fontSize: '1rem',
        cursor: 'pointer',
        color: darculaTheme.fg,
    },
};

/** React inline `style` objects for PagesTooltip (SSAP flow page list). */
export const darculaPagesTooltipStyles = {
    blockWrapperAdditionalStyles: {
        width: 550,
        overflowY: 'auto',
        height: '460px',
    },
    rowHoverBackground: darculaTheme.button,
    rowDefaultBackground: 'transparent',
    currentPageHighlightBackground: 'rgba(204, 120, 50, 0.35)',
    titleStyle: {
        marginBottom: '10px',
        fontSize: '1.5rem',
        fontWeight: 600,
        color: darculaTheme.fg,
    },
    rowBaseStyle: {
        position: 'relative',
        fontSize: '1.2rem',
        cursor: 'pointer',
    },
    inactiveFlowTextColor: '#BC6B6B',
    hhmBadgeOffsetStyle: {
        position: 'absolute',
        left: '30px',
    },
    pageNameOffsetStyle: {
        position: 'absolute',
        left: '60px',
    },
    componentNameOffsetStyle: {
        position: 'absolute',
        left: '380px',
        color: darculaTheme.lightPurple,
    },
};

/** React inline `style` objects for FlagsTooltip (per-HHM flag buckets / masks). */
export const darculaFlagsTooltipStyles = {
    rowHoverBackground: darculaTheme.button,
    rowDefaultBackground: 'transparent',
    titleStyle: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: darculaTheme.fg,
        marginBottom: '8px',
    },
    memberHeaderRowStyle: {
        margin: '10px 0',
    },
    memberNameStyle: {
        color: darculaTheme.keywordOrange,
        cursor: 'pointer',
        fontSize: '1.3rem',
    },
    masksToggleStyleBase: {
        display: 'inline-block',
        marginRight: '10px',
        cursor: 'pointer',
        fontSize: '1.2rem',
    },
    masksToggleExpandedColor: '#BC6B6B',
    masksToggleCollapsedColor: '#7FB87F',
    maskTypeRowStyle: {
        color: darculaTheme.keywordOrange,
        padding: '5px 0',
        fontSize: '1.2rem',
    },
    maskTypeFlagsValueStyle: {
        cursor: 'pointer',
    },
    flagDetailRowStyle: {
        position: 'relative',
    },
    flagDetailLabelStyle: {
        fontSize: '1.2rem',
        color: darculaTheme.fg,
    },
    flagDetailValueStyle: {
        fontSize: '1rem',
        position: 'absolute',
        left: '200px',
        cursor: 'pointer',
        paddingTop: '5px',
        color: darculaTheme.fg,
    },
};

/** React inline `style` objects for AttestationsInfo (per-state attestation visibility). */
const attestationHiddenOnDarkColor = '#E8A0A0';

export const darculaAttestationsInfoStyles = {
    blockWrapperAdditionalStyles: {
        width: 320,
    },
    titleBlockStyle: {
        fontSize: '1.5rem',
        marginTop: '1rem',
        lineHeight: 1.5,
        color: darculaTheme.fg,
    },
    emphasisValueStyle: {
        fontWeight: 700,
        color: darculaTheme.borderAccent,
    },
    listContainerStyle: {
        marginTop: '1rem',
    },
    attestationRowStyle: {
        marginBottom: '0.35rem',
    },
    /** Visible attestation: primary foreground + weight so list stays readable. */
    attestationNameVisibleStyle: {
        fontSize: '1.2rem',
        fontWeight: 600,
        color: darculaTheme.fg,
    },
    attestationNameHiddenStyle: {
        fontSize: '1.2rem',
        fontWeight: 600,
        color: attestationHiddenOnDarkColor,
    },
};

/** Dev-only SSAP data inconsistency alarm banner (fixed to viewport top while scrolling). */
export const darculaSsapDataInconsistencyBannerStyles = {
    bannerShellStyle: {
        minHeight: '68px',
        marginBottom: '10px',
        boxSizing: 'border-box',
    },
    bannerStyle: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 2400,
        boxSizing: 'border-box',
        padding: '6px 10px',
        borderTop: '3px solid #000',
        borderBottom: '3px solid #000',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.55), 0 0 24px rgba(255, 23, 68, 0.35)',
    },
    bannerContentStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '14px',
        padding: '10px 16px',
        backgroundColor: '#0d0d0d',
        color: '#f5c400',
        border: '2px solid #000',
        borderRadius: '4px',
        boxShadow: 'inset 0 0 0 1px rgba(245, 196, 0, 0.35), inset 0 -8px 24px rgba(255, 23, 68, 0.12)',
    },
    bannerTextBlockStyle: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    bannerHeadlineStyle: {
        fontWeight: 800,
        fontSize: '13px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#fff176',
        textShadow: '0 0 8px rgba(255, 241, 118, 0.45)',
    },
    bannerSublineStyle: {
        fontWeight: 600,
        fontSize: '11px',
        letterSpacing: '0.03em',
        textTransform: 'none',
        color: '#e0c040',
        opacity: 0.95,
    },
    bannerCountBadgeStyle: {
        flexShrink: 0,
        padding: '4px 10px',
        borderRadius: '999px',
        backgroundColor: '#ff1744',
        color: '#fff',
        fontWeight: 800,
        fontSize: '11px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        border: '2px solid #000',
        boxShadow: '0 0 10px rgba(255, 23, 68, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
    },
    keyframesCss: `
        @keyframes ssap-warning-stripe-scroll {
            0% { background-position: 0 0; }
            100% { background-position: 48px 0; }
        }
        @keyframes ssap-banner-glow-pulse {
            0%, 100% {
                box-shadow: 0 6px 20px rgba(0, 0, 0, 0.55), 0 0 18px rgba(255, 23, 68, 0.25);
            }
            50% {
                box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6), 0 0 32px rgba(255, 23, 68, 0.55), 0 0 48px rgba(245, 196, 0, 0.2);
            }
        }
        @keyframes ssap-banner-content-flash {
            0%, 88%, 100% {
                box-shadow: inset 0 0 0 1px rgba(245, 196, 0, 0.35), inset 0 -8px 24px rgba(255, 23, 68, 0.12);
            }
            92% {
                box-shadow: inset 0 0 0 2px rgba(255, 241, 118, 0.9), inset 0 0 20px rgba(255, 23, 68, 0.35);
            }
        }
        @keyframes ssap-beacon-rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        @keyframes ssap-inconsistency-beacon-core {
            0%, 100% {
                transform: scale(1);
                box-shadow: 0 0 8px #f5c400, 0 0 16px #ff6f00;
            }
            50% {
                transform: scale(1.08);
                box-shadow: 0 0 12px #fff176, 0 0 24px #ff1744;
            }
        }
        @keyframes ssap-inconsistency-beacon-ring {
            0% {
                transform: scale(0.5);
                opacity: 0.9;
                border-color: rgba(255, 23, 68, 0.95);
            }
            100% {
                transform: scale(2.8);
                opacity: 0;
                border-color: rgba(245, 196, 0, 0.4);
            }
        }
        @keyframes ssap-count-badge-pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.06); }
        }
        .ssap-inconsistency-banner {
            background: repeating-linear-gradient(
                -45deg,
                #f5c400 0,
                #f5c400 12px,
                #ff1744 12px,
                #ff1744 24px,
                #1a1a1a 24px,
                #1a1a1a 36px
            );
            background-size: 48px 48px;
            animation:
                ssap-warning-stripe-scroll 1.1s linear infinite,
                ssap-banner-glow-pulse 2s ease-in-out infinite;
        }
        .ssap-inconsistency-banner__content {
            animation: ssap-banner-content-flash 4s ease-in-out infinite;
        }
        .ssap-inconsistency-banner__count {
            animation: ssap-count-badge-pulse 1.8s ease-in-out infinite;
        }
        .ssap-inconsistency-beacon {
            position: relative;
            width: 34px;
            height: 34px;
            flex-shrink: 0;
            border-radius: 50%;
            background: #111;
            box-shadow:
                0 0 0 2px #000,
                0 0 14px rgba(255, 23, 68, 0.65),
                inset 0 0 8px rgba(0, 0, 0, 0.8);
            overflow: hidden;
        }
        .ssap-inconsistency-beacon__rotor {
            position: absolute;
            inset: -40%;
            background: conic-gradient(
                from 0deg,
                transparent 0deg 250deg,
                rgba(255, 23, 68, 0.15) 250deg,
                rgba(255, 23, 68, 0.95) 270deg,
                rgba(245, 196, 0, 1) 285deg,
                rgba(255, 241, 118, 0.9) 295deg,
                transparent 310deg 360deg
            );
            animation: ssap-beacon-rotate 2.5s linear infinite;
        }
        .ssap-inconsistency-beacon__core {
            position: absolute;
            inset: 7px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: radial-gradient(circle at 35% 30%, #fff 0%, #f5c400 40%, #ff6f00 85%);
            color: #1a1a1a;
            font-weight: 900;
            font-size: 14px;
            line-height: 1;
            z-index: 1;
            animation: ssap-inconsistency-beacon-core 0.85s ease-in-out infinite;
        }
        .ssap-inconsistency-beacon__ring {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            border: 2px solid rgba(255, 23, 68, 0.85);
            z-index: 2;
            pointer-events: none;
            animation: ssap-inconsistency-beacon-ring 1.4s ease-out infinite;
        }
        .ssap-inconsistency-beacon__ring--delayed {
            animation-delay: 0.45s;
        }
        .ssap-inconsistency-beacon__ring--delayed-2 {
            animation-delay: 0.9s;
        }
    `,
    beaconLabel: 'Data inconsistency alert',
};

/** React inline `style` objects for DataInconsistenciesInfo (SSAP data quality dev tool). */
export const darculaDataInconsistenciesInfoStyles = {
    blockWrapperAdditionalStyles: {
        width: 520,
        maxHeight: '70vh',
    },
    titleBlockStyle: {
        marginTop: '1rem',
        lineHeight: 1.5,
        color: darculaTheme.fg,
    },
    emphasisValueStyle: {
        fontWeight: 700,
        color: darculaTheme.keywordOrange,
    },
    listContainerStyle: {
        marginTop: '1rem',
    },
    ruleGroupStyle: {
        marginBottom: '0.75rem',
        border: `1px solid ${darculaTheme.border}`,
        borderRadius: '4px',
        overflow: 'hidden',
    },
    ruleHeaderButtonStyle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        padding: '8px 10px',
        backgroundColor: darculaTheme.bgInput,
        border: 'none',
        color: darculaTheme.fg,
        fontWeight: 700,
        fontSize: '14px',
        textAlign: 'left',
        cursor: 'pointer',
    },
    ruleToggleGlyphStyle: {
        minWidth: '1.25rem',
        textAlign: 'center',
        color: darculaTheme.borderAccent,
    },
    ruleDescriptionStyle: {
        padding: '8px 10px 0',
        color: darculaTheme.fg,
        opacity: 0.85,
        fontSize: '13px',
        lineHeight: 1.4,
    },
    ruleIssuesContainerStyle: {
        padding: '8px 10px 10px',
    },
    issueRowStyle: {
        marginBottom: '0.75rem',
        paddingBottom: '0.75rem',
        borderBottom: `1px solid ${darculaTheme.border}`,
    },
    issueRowLastStyle: {
        marginBottom: 0,
        paddingBottom: 0,
        borderBottom: 'none',
    },
    issueMessageStyle: {
        fontWeight: 600,
        color: darculaTheme.fg,
        marginBottom: '0.35rem',
        fontSize: '13px',
        lineHeight: 1.4,
    },
    pathStyle: {
        marginTop: '0.35rem',
        fontFamily: 'monospace',
        fontSize: '12px',
        color: darculaTheme.borderAccent,
    },
};

/** React inline `style` objects for IdsInfo (application + HHM identifiers). */
export const darculaIdsInfoStyles = {
    blockWrapperAdditionalStyles: {
        width: 450,
    },
    applicationIdRowStyle: {
        fontSize: '1.5rem',
        marginTop: '1rem',
        color: darculaTheme.fg,
    },
    applicationIdValueStyle: {
        fontWeight: 700,
        color: darculaTheme.keywordOrange,
    },
    memberListContainerStyle: {
        marginTop: '1rem',
    },
    memberRowStyle: {
        position: 'relative',
        marginBottom: '0.35rem',
    },
    memberNameStyle: {
        fontSize: '1.2rem',
        color: darculaTheme.keywordOrange,
    },
    memberPersonIdStyle: {
        fontSize: '1.2rem',
        position: 'absolute',
        left: '250px',
        color: darculaTheme.fg,
    },
    memberGuidStyle: {
        fontSize: '1.2rem',
        position: 'absolute',
        left: '300px',
        color: darculaTheme.lightPurple,
    },
};

const bloodRelationWarningColor = '#BC6B6B';

/** React inline `style` objects for BloodRelationshipsInfo (tax household BR chips + hover tooltip). */
export const darculaBloodRelationshipsInfoStyles = {
    blockWrapperAdditionalStyles: {
        overflowY: 'auto',
        height: '400px',
    },
    relationTooltipContentStyle: {
        fontSize: '1.05rem',
        lineHeight: 1.45,
        color: darculaTheme.fg,
    },
    relationTooltipPersonNameStyle: {
        fontSize: '1.15rem',
        fontWeight: 600,
        color: darculaTheme.keywordOrange,
    },
    relationTooltipMutedStyle: {
        color: darculaTheme.fg,
        opacity: 0.85,
    },
    relationTypeTextStyle: {
        marginTop: '0.35rem',
        fontSize: '1rem',
        fontWeight: 600,
        color: bloodRelationWarningColor,
    },
    brChipBaseStyle: {
        float: 'left',
        borderRadius: '6px',
        padding: '6px 8px',
        margin: '4px',
        fontSize: '0.95rem',
        lineHeight: 1.35,
        borderStyle: 'solid',
        borderWidth: 1,
        cursor: 'default',
    },
    brChipBackgroundMissingRelation: {
        backgroundColor: darculaTheme.bgInput,
        borderColor: bloodRelationWarningColor,
        boxShadow: `inset 0 0 0 1px ${bloodRelationWarningColor}33`,
    },
    brChipBackgroundHasRelation: {
        backgroundColor: darculaTheme.bgInput,
        borderColor: darculaTheme.borderAccent,
    },
    brPropRowStyle: {
        display: 'block',
        color: darculaTheme.fg,
        fontSize: '0.95rem',
    },
    brPropRowNullStyle: {
        display: 'block',
        color: bloodRelationWarningColor,
        fontSize: '0.95rem',
    },
    hoverTooltipPanelStyle: {
        position: 'fixed',
        width: 'min(320px, 90vw)',
        minHeight: '100px',
        maxWidth: '320px',
        backgroundColor: darculaTheme.bgInput,
        borderRadius: '0.5rem',
        padding: '12px 14px',
        border: `1px solid ${darculaTheme.borderAccent}`,
        zIndex: 50000,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        color: darculaTheme.fg,
    },
    groupChunkStyle: {
        float: 'left',
        borderRadius: '6px',
        padding: '6px',
        margin: '6px',
        fontSize: '0.95rem',
        lineHeight: 1.35,
        backgroundColor: darculaTheme.bg,
        border: `1px solid ${darculaTheme.border}`,
        boxShadow: `inset 0 1px 0 ${darculaTheme.border}66`,
    },
};

const escDiagnosticPositiveColor = '#7FB87F';
const escDiagnosticNegativeColor = '#BC6B6B';

/** React inline `style` objects for EscPageRenderInfo (ESC skip diagnostics per HHM). */
export const darculaEscPageRenderInfoStyles = {
    blockWrapperAdditionalStyles: {
        width: 515,
    },
    noteBannerStyle: {
        fontSize: '14px',
        marginBottom: '1.5rem',
        lineHeight: 1.45,
        fontWeight: 600,
        color: darculaTheme.keywordOrange,
    },
    memberGridStyle: {
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        marginBottom: '1rem',
        columnGap: '13px',
        rowGap: '5px',
        alignItems: 'start',
    },
    memberNameCellStyle: {
        fontSize: '14px',
        marginBottom: '1rem',
        color: darculaTheme.keywordOrange,
    },
    toggleButtonStyle: {
        backgroundColor: darculaTheme.button,
        border: `1px solid ${darculaTheme.border}`,
        color: darculaTheme.fg,
        borderRadius: '4px',
        cursor: 'pointer',
        padding: '4px 10px',
        fontSize: '14px',
        lineHeight: 1.25,
    },
    toggleGlyphBoxStyle: {
        minWidth: '2.1rem',
        textAlign: 'center',
    },
    gridLabelCellStyle: {
        color: darculaTheme.fg,
        fontSize: '14px',
    },
    gridValueCellStyle: {
        color: darculaTheme.fg,
        fontSize: '14px',
    },
    booleanPositiveStyle: {
        color: escDiagnosticPositiveColor,
        fontWeight: 600,
        fontSize: '14px',
    },
    booleanNegativeStyle: {
        color: escDiagnosticNegativeColor,
        fontWeight: 600,
        fontSize: '14px',
    },
    comparisonMatchLabelStyle: {
        color: darculaTheme.borderAccent,
        fontWeight: 600,
        fontSize: '14px',
    },
    comparisonMismatchLabelStyle: {
        color: escDiagnosticNegativeColor,
        fontWeight: 600,
        fontSize: '14px',
    },
};
